import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { userService } from "../services/user.service";
import type {
	CreateUserRequest,
	ErrorResponse,
	PaginationParams,
	UpdateUserRequest,
} from "../types";

// ─── Query Keys ─────────────────────────────────────────────────────
export const userKeys = {
	all: ["users"] as const,
	lists: () => [...userKeys.all, "list"] as const,
	list: (params?: PaginationParams) => [...userKeys.lists(), params] as const,
	details: () => [...userKeys.all, "detail"] as const,
	detail: (id: string) => [...userKeys.details(), id] as const,
};

// ─── useUsers (list) ────────────────────────────────────────────────
export function useUsers(params?: PaginationParams, options?: { enabled?: boolean }) {
	return useQuery({
		queryKey: userKeys.list(params),
		queryFn: () => userService.list(params),
		enabled: options?.enabled ?? true,
	});
}

// ─── useUser (getById) ──────────────────────────────────────────────
export function useUser(id: string) {
	return useQuery({
		queryKey: userKeys.detail(id),
		queryFn: () => userService.getById(id),
		enabled: !!id,
	});
}

// ─── useCreateUser ──────────────────────────────────────────────────
export function useCreateUser() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateUserRequest) => userService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userKeys.lists() });
			message.success("Foydalanuvchi muvaffaqiyatli yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Foydalanuvchi yaratishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateUser ──────────────────────────────────────────────────
export function useUpdateUser() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateUserRequest }) =>
			userService.update(id, data),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: userKeys.lists() });
			queryClient.invalidateQueries({
				queryKey: userKeys.detail(variables.id),
			});
			message.success("Foydalanuvchi muvaffaqiyatli yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Foydalanuvchi yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteUser ──────────────────────────────────────────────────
export function useDeleteUser() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => userService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: userKeys.lists() });
			message.success("Foydalanuvchi muvaffaqiyatli o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Foydalanuvchi o'chirishda xatolik";
			message.error(msg);
		},
	});
}
